import { Component } from '@theme/component';
import { sectionRenderer } from '@theme/section-renderer';
import { requestIdleCallback, viewTransition } from '@theme/utilities';
import { ThemeEvents } from '@theme/events';
import { PaginatedListAspectRatioHelper } from '@theme/paginated-list-aspect-ratio';

/**
 * A custom element that renders a paginated list of items.
 *
 * @typedef {object} Refs
 * @property {HTMLUListElement} [grid] - The grid element.
 * @property {HTMLSpanElement} [viewMorePrevious] - The view more previous button.
 * @property {HTMLSpanElement} [viewMoreNext] - The view more next button.
 * @property {HTMLElement[]} [cards] - The cards elements.
 *
 * @extends Component<Refs>
 */
export default class PaginatedList extends Component {
  /**
   * @type {Map<number, string>}
   */
  pages = new Map();

  /** @type {IntersectionObserver | undefined} */
  infinityScrollObserver;

  /** @type {((value: void) => void) | null} */
  #resolveNextPagePromise = null;

  /** @type {((value: void) => void) | null} */
  #resolvePreviousPagePromise = null;

  /** @type {PaginatedListAspectRatioHelper} */
  #aspectRatioHelper;

  connectedCallback() {
    super.connectedCallback();

    /**
     * If user opens:
     * /collections/earrings?page=2
     *
     * Browser URL will become:
     * /collections/earrings
     *
     * This prevents duplicate products after reload.
     */
    this.#removePageParamFromUrl();

    /** @type {HTMLElement | null} */
    const templateCard = this.querySelector('[ref="cardGallery"]');

    if (templateCard) {
      this.#aspectRatioHelper = new PaginatedListAspectRatioHelper({
        templateCard,
      });
    }

    this.#fetchPage('next');
    this.#fetchPage('previous');
    this.#observeViewMore();

    // Listen for filter updates to clear cached pages
    document.addEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.infinityScrollObserver) {
      this.infinityScrollObserver.disconnect();
    }

    document.removeEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

  /**
   * Clean current URL by removing ?page= and hash.
   *
   * @returns {URL}
   */
  #getCleanUrl() {
    const url = new URL(window.location.href);

    url.searchParams.delete('page');
    url.hash = '';

    return url;
  }

  /**
   * URL used for AJAX fetching only.
   *
   * Example:
   * /collections/earrings?page=2
   *
   * @param {number} pageNumber
   * @returns {URL}
   */
  #getFetchUrl(pageNumber) {
    const url = this.#getCleanUrl();

    url.searchParams.set('page', pageNumber.toString());
    url.hash = '';

    return url;
  }

  /**
   * URL shown in browser only.
   *
   * Example:
   * /collections/earrings#page-2
   *
   * @param {number} pageNumber
   * @returns {URL}
   */
  #getDisplayUrl(pageNumber) {
    const url = this.#getCleanUrl();

    if (pageNumber > 1) {
      url.hash = `page-${pageNumber}`;
    }

    return url;
  }

  /**
   * Remove ?page= from browser URL on direct page load/reload.
   */
  #removePageParamFromUrl() {
    const url = new URL(window.location.href);

    if (url.searchParams.has('page')) {
      url.searchParams.delete('page');
      history.replaceState('', '', url.toString());
    }
  }

  /**
   * Get currently rendered page numbers from real grid DOM.
   * This is stronger than depending only on this.refs.cards,
   * because refs may not update immediately after infinite append.
   *
   * @returns {number[]}
   */
  #getRenderedPageNumbers() {
    const { grid } = this.refs;

    if (!grid) return [];

    return Array.from(grid.querySelectorAll('[ref="cards[]"]'))
      .map((card) => Number(card.dataset.page))
      .filter((page) => !Number.isNaN(page) && page > 0);
  }

  #observeViewMore() {
    const { viewMorePrevious, viewMoreNext } = this.refs;

    if (!viewMorePrevious && !viewMoreNext) return;

    if (!this.infinityScrollObserver) {
      this.infinityScrollObserver = new IntersectionObserver(
        async (entries) => {
          if (viewTransition.current) await viewTransition.current;

          for (const entry of entries) {
            if (entry.isIntersecting) {
              const { viewMorePrevious, viewMoreNext } = this.refs;

              if (entry.target === viewMorePrevious) {
                this.#renderPreviousPage();
              } else if (entry.target === viewMoreNext) {
                this.#renderNextPage();
              }
            }
          }
        },
        {
          rootMargin: '100px',
        }
      );
    }

    if (viewMorePrevious) {
      this.infinityScrollObserver.observe(viewMorePrevious);
    }

    if (viewMoreNext) {
      this.infinityScrollObserver.observe(viewMoreNext);
    }
  }

  /**
   * @param {{ page: number, url?: URL } | undefined} pageInfo
   * @returns {boolean}
   */
  #shouldUsePage(pageInfo) {
    if (!pageInfo) return false;

    const { grid } = this.refs;
    const lastPage = grid?.dataset.lastPage;

    if (!lastPage || pageInfo.page < 1 || pageInfo.page > Number(lastPage)) {
      return false;
    }

    return true;
  }

  /**
   * @param {"previous" | "next"} type
   */
  async #fetchPage(type) {
    const page = this.#getPage(type);

    const resolvePromise = () => {
      if (type === 'next') {
        this.#resolveNextPagePromise?.();
        this.#resolveNextPagePromise = null;
      } else {
        this.#resolvePreviousPagePromise?.();
        this.#resolvePreviousPagePromise = null;
      }
    };

    if (!page || !this.#shouldUsePage(page)) {
      resolvePromise();
      return;
    }

    await this.#fetchSpecificPage(page.page, page.url);

    resolvePromise();
  }

  /**
   * @param {number} pageNumber
   * @param {URL} [url]
   */
  async #fetchSpecificPage(pageNumber, url = undefined) {
    const pageInfo = {
      page: pageNumber,
      url,
    };

    if (!url) {
      pageInfo.url = this.#getFetchUrl(pageNumber);
    }

    if (!this.#shouldUsePage(pageInfo)) return;

    const pageContent = await sectionRenderer.getSectionHTML(
      this.sectionId,
      true,
      pageInfo.url
    );

    this.pages.set(pageNumber, pageContent);
  }

  async #renderNextPage() {
    const { grid } = this.refs;

    if (!grid) return;

    const nextPage = this.#getPage('next');

    if (!nextPage || !this.#shouldUsePage(nextPage)) return;

    let nextPageItemElements = this.#getGridForPage(nextPage.page);

    if (!nextPageItemElements) {
      const promise = new Promise((res) => {
        this.#resolveNextPagePromise = res;
      });

      this.#fetchPage('next');

      await promise;

      nextPageItemElements = this.#getGridForPage(nextPage.page);

      if (!nextPageItemElements) return;
    }

    grid.append(...nextPageItemElements);

    this.#aspectRatioHelper.processNewElements();

    /**
     * Update browser URL as #page-2, #page-3, etc.
     * We calculate from actual rendered grid items.
     */
    const renderedPages = this.#getRenderedPageNumbers();
    const maxRenderedPage = renderedPages.length
      ? Math.max(...renderedPages, nextPage.page)
      : nextPage.page;

    history.pushState('', '', this.#getDisplayUrl(maxRenderedPage).toString());

    requestIdleCallback(() => {
      this.#fetchPage('next');
    });
  }

  async #renderPreviousPage() {
    const { grid } = this.refs;

    if (!grid) return;

    const previousPage = this.#getPage('previous');

    if (!previousPage || !this.#shouldUsePage(previousPage)) return;

    let previousPageItemElements = this.#getGridForPage(previousPage.page);

    if (!previousPageItemElements) {
      const promise = new Promise((res) => {
        this.#resolvePreviousPagePromise = res;
      });

      this.#fetchPage('previous');

      await promise;

      previousPageItemElements = this.#getGridForPage(previousPage.page);

      if (!previousPageItemElements) return;
    }

    const scrollTop = window.scrollY;
    const firstElement = grid.firstElementChild;
    const oldHeight = firstElement
      ? firstElement.getBoundingClientRect().top + window.scrollY
      : 0;

    grid.prepend(...previousPageItemElements);

    this.#aspectRatioHelper.processNewElements();

    /**
     * Update browser URL as #page-1, #page-2, etc.
     * For page 1, hash will automatically be removed.
     */
    const renderedPages = this.#getRenderedPageNumbers();
    const minRenderedPage = renderedPages.length
      ? Math.min(...renderedPages, previousPage.page)
      : previousPage.page;

    history.pushState('', '', this.#getDisplayUrl(minRenderedPage).toString());

    if (firstElement) {
      const newHeight = firstElement.getBoundingClientRect().top + window.scrollY;
      const heightDiff = newHeight - oldHeight;

      window.scrollTo({
        top: scrollTop + heightDiff,
        behavior: 'instant',
      });
    }

    requestIdleCallback(() => {
      this.#fetchPage('previous');
    });
  }

  /**
   * @param {"previous" | "next"} type
   * @returns {{ page: number, url: URL } | undefined}
   */
  #getPage(type) {
    const isPrevious = type === 'previous';

    /**
     * Important:
     * Do not depend on this.refs.cards here.
     * Newly appended cards may not be reflected in refs immediately.
     */
    const renderedPages = this.#getRenderedPageNumbers();

    if (!renderedPages.length) return;

    const currentMinPage = Math.min(...renderedPages);
    const currentMaxPage = Math.max(...renderedPages);

    const page = isPrevious ? currentMinPage - 1 : currentMaxPage + 1;

    const url = this.#getFetchUrl(page);

    return {
      page,
      url,
    };
  }

  /**
   * @param {number} page
   * @returns {NodeListOf<Element> | undefined}
   */
  #getGridForPage(page) {
    const pageHTML = this.pages.get(page);

    if (!pageHTML) return;

    const parsedPage = new DOMParser().parseFromString(pageHTML, 'text/html');
    const gridElement = parsedPage.querySelector('[ref="grid"]');

    if (!gridElement) return;

    return gridElement.querySelectorAll(':scope > [ref="cards[]"]');
  }

  get sectionId() {
    const id = this.getAttribute('section-id');

    if (!id) throw new Error('The section-id attribute is required');

    return id;
  }

  /**
   * Handle filter updates by clearing cached pages
   */
  #handleFilterUpdate = () => {
    this.pages.clear();

    this.#resolveNextPagePromise?.();
    this.#resolvePreviousPagePromise?.();

    this.#resolveNextPagePromise = null;
    this.#resolvePreviousPagePromise = null;

    const currentLastPage = this.refs.grid?.dataset.lastPage;

    const observer = new MutationObserver(() => {
      const newLastPage = this.refs.grid?.dataset.lastPage;

      if (newLastPage !== currentLastPage) {
        observer.disconnect();

        if (!this.isConnected) {
          return;
        }

        this.#observeViewMore();
        this.#fetchPage('next');
      }
    });

    const { grid } = this.refs;

    if (grid) {
      observer.observe(grid, {
        attributes: true,
        attributeFilter: ['data-last-page'],
        childList: true,
      });

      setTimeout(() => {
        if (observer) {
          observer.disconnect();
        }
      }, 3000);
    }
  };
}